<script lang="ts">
	import { onNavigate } from '$app/navigation';
	import type { dbOrderUnavailableDatesType } from '$lib/dbConnections.server';
	import Holidays from 'date-holidays';
	import { cart } from '$stores/cart';

	export let chosenDate: string;
	let dialog: HTMLDialogElement;
	let monthDetails: HTMLDetailsElement;
	let yearDetails: HTMLDetailsElement;
	let usersDay = -1;
	let usersMonth = new Date().getMonth();
	let usersYear = new Date().getFullYear();
	let daysIndex: { [key: number]: number } = {
		1: 0,
		2: 1,
		3: 2,
		4: 3,
		5: 4,
		6: 5,
		0: 6
	};
	let daysInMonths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
	let monthDays: Array<
		Array<{ text: string; available: boolean; tooltip: string }>
	> = [];
	let monthSelect: HTMLSelectElement;
	let unavailableDates: Array<any>;
	let dbUnavailableDatesUnfiltered: Array<any> = [];
	let dbUnavailableDates: Array<any> = [];
	let loading: boolean = true;

	getDbUnavailableDates();

	async function getDbUnavailableDates() {
		let response = await fetch('/api/getUnavailableDates', {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json'
			}
		});
		response = await response.json();

		if (response.status === 200) {
			dbUnavailableDatesUnfiltered =
				response.body as unknown as dbOrderUnavailableDatesType[];
		} else {
			dbUnavailableDatesUnfiltered = [];
		}
		loading = false;
	}

	function getManualUnavailableDates() {
		let tempDbUnavailableDates = dbUnavailableDatesUnfiltered.filter((date) => {
			if (
				date.year == usersYear &&
				(($cart.delivery === true && date.delivery === false) ||
					($cart.delivery === false && date.collection === false))
			) {
				return true;
			} else {
				return false;
			}
		});
		dbUnavailableDates = tempDbUnavailableDates.map((tempDateObject) => {
			return {
				date:
					tempDateObject.year +
					'-' +
					tempDateObject.month +
					'-' +
					tempDateObject.day +
					' 00:00:00',
				type: 'manual',
				name: checkTempDateObject(tempDateObject)
			};
		});
		return dbUnavailableDates;
	}

	function checkTempDateObject(tempDateObject: dbOrderUnavailableDatesType) {
		if (
			tempDateObject.delivery === false &&
			tempDateObject.collection === false
		) {
			return 'Unavailable';
		} else if (tempDateObject.delivery === false) {
			return 'Delivery unavailable';
		} else if (tempDateObject.collection === false) {
			return 'Collection unavailable';
		}
	}

	$: if (!loading) {
		unavailableDates = getUnavailableDates();
		dbUnavailableDates = getManualUnavailableDates();

		if (dbUnavailableDates.length > 0) {
			unavailableDates = unavailableDates.concat(dbUnavailableDates);
		}

		monthDays = Array.from({ length: 6 }, () =>
			Array.from(
				{ length: 7 },
				() =>
					new Object({ text: '', available: false, tooltip: '' }) as {
						text: string;
						available: boolean;
						tooltip: string;
					}
			)
		);
		let tempDay = 1;
		let tempDate = new Date().setDate(new Date().getDate() + 2);
		for (
			let i = daysIndex[new Date(usersYear, usersMonth, 1).getDay()];
			i < 7;
			i++
		) {
			if (
				new Date(usersYear + '-' + (usersMonth + 1) + '-' + tempDay) <
				new Date(tempDate)
			) {
				monthDays[0][i] = {
					text: tempDay.toString(),
					available: false,
					tooltip: ''
				};
			} else {
				let dateAvailability = checkDateAvailability(
					usersYear,
					usersMonth,
					tempDay
				);
				monthDays[0][i] = {
					text: tempDay.toString(),
					available: dateAvailability.available,
					tooltip: dateAvailability.tooltip as string
				};
			}
			tempDay++;
		}
		for (let i = 1; i < 6; i++) {
			for (let j = 0; j < 7; j++) {
				if (tempDay <= daysInMonths[usersMonth]) {
					if (
						new Date(usersYear + '-' + (usersMonth + 1) + '-' + tempDay) <
						new Date(tempDate)
					) {
						monthDays[i][j] = {
							text: tempDay.toString(),
							available: false,
							tooltip: 'Unavailable'
						};
					} else {
						let dateAvailability = checkDateAvailability(
							usersYear,
							usersMonth,
							tempDay
						);
						monthDays[i][j] = {
							text: tempDay.toString(),
							available: dateAvailability.available,
							tooltip: dateAvailability.tooltip
						};
					}
					tempDay++;
				}
			}
		}
		let removeLastRow = true;
		for (let i = 0; i < 7; i++) {
			if (monthDays[5][i].text != '') {
				removeLastRow = false;
			}
		}
		if (removeLastRow) {
			monthDays.pop();
		}
	}

	function checkDateAvailability(year: number, month: number, day: number) {
		let date =
			year +
			'-' +
			(month + 1).toString().padStart(2, '0') +
			'-' +
			day.toString().padStart(2, '0');
		let dateName = unavailableDates.find(
			(tempDate) =>
				tempDate.date.split(' ')[0] === date &&
				(tempDate.type === 'bank' ||
					tempDate.type === 'public' ||
					tempDate.type === 'manual')
		)?.name;

		//Checks if the date is a Sunday
		let dayOfWeek = new Date(year, month, day).getDay();
		if (dayOfWeek === 0) {
			return {
				name: day,
				available: false,
				tooltip: 'Unavailable'
			};
		}

		return {
			name: day,
			available: dateName === undefined ? true : false,
			tooltip: dateName || 'Unavailable'
		};
	}

	function toggleMonthDetails() {
		monthDetails.open = !monthDetails.open;
		if (monthDetails.open === true && yearDetails.open === true)
			yearDetails.open = false;
	}

	function toggleYearDetails() {
		yearDetails.open = !yearDetails.open;
		if (yearDetails.open === true && monthDetails.open === true)
			monthDetails.open = false;
	}

	$: {
		// Check for leap year
		if ((usersYear % 4 == 0 && usersYear % 100 != 0) || usersYear % 400 == 0) {
			daysInMonths[1] = 29;
		} else {
			daysInMonths[1] = 28;
		}
	}

	onNavigate(() => {
		if (dialog.open) {
			dialog.close();
		}
	});

	function openDialog() {
		dialog.showModal();
	}

	function closeDialog() {
		dialog.close();
	}

	function checkDate(year: number, month: number, day: string) {
		chosenDate = year + '-' + (month + 1) + '-' + day;
		closeDialog();
	}

	$: {
		if (
			usersYear == new Date().getFullYear() &&
			usersMonth <= new Date().getMonth()
		) {
			let tempDate = new Date();
			tempDate.setDate(tempDate.getDate() + 2);
			usersMonth = tempDate.getMonth();
		}
	}

	function changeMonth(direction: number) {
		usersMonth += direction;
		if (usersMonth < 0) {
			usersMonth = 11;
			usersYear--;
		} else if (usersMonth > 11) {
			usersMonth = 0;
			usersYear++;
		}
	}

	function getUnavailableDates() {
		let hd = new Holidays();
		hd.init('IE');
		let holidays = hd.getHolidays(usersYear);
		if (usersYear === 2024) {
			//Removes the last holiday in 2024 as it is wrong and I have opened an issue to get it fixed
			//It was saying the Christmas bank holiday was on the 27th of December
			// https://github.com/commenthol/date-holidays/issues/495
			holidays.pop();
		}
		return holidays;
	}

	function closeDialogWhenClickedOutside(
		event: MouseEvent,
		clickedDialog: HTMLDialogElement
	) {
		if (event.target === clickedDialog) {
			clickedDialog.close();
		}
	}
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
<dialog
	bind:this={dialog}
	class="space-y-2 rounded-lg border border-gray-200 p-4 text-sm shadow-lg"
	on:click={(event) => closeDialogWhenClickedOutside(event, dialog)}
>
	<div class="flex justify-end">
		<button
			aria-label="Close cart"
			on:click={() => dialog.close()}
			class="text-logoBackgroundBlue hover:text-logobackgroundBlueRgba text-base"
			><span class="fa-duotone fa-circle-xmark"></span></button
		>
	</div>

	<div class="flex justify-between">
		<button
			on:click={() => changeMonth(-1)}
			disabled={usersYear == new Date().getFullYear() &&
				usersMonth == new Date().getMonth()}
			aria-label="Previous Month"
			class="text-logoBackgroundBlue text-lg font-medium disabled:text-gray-300"
		>
			<span class="fa-solid fa-angle-left"></span>
		</button>

		<div class="flex gap-1">
			<details class="relative" bind:this={monthDetails}>
				<summary
					on:click|preventDefault={toggleMonthDetails}
					style="-webkit-details-marker: none; list-style-type: none;"
					class="text-lg font-medium text-gray-900 hover:text-gray-600"
					>{new Date(0, usersMonth).toLocaleString('default', {
						month: 'long'
					})}</summary
				>
				<section class="absolute z-10 rounded border border-gray-300">
					<fieldset
						class="flex max-h-[25vh] flex-col justify-between space-y-2 overflow-auto rounded bg-white p-2"
					>
						{#each Array.from({ length: 12 }, (_, i) => i) as month}
							{#if (new Date().getMonth() < month && usersYear == new Date().getFullYear()) || (new Date().getMonth() == month && new Date().getFullYear() == usersYear && new Date().getDate() + 2 < daysInMonths[month]) || usersYear > new Date().getFullYear()}
								<div class="flex gap-2">
									<button
										aria-current={usersMonth == month}
										on:click={() => {
											usersMonth = month;
											toggleMonthDetails();
											//Sets focus to the summary element
											monthDetails.querySelector('summary')?.focus();
										}}
										>{new Date(0, month).toLocaleString('default', {
											month: 'long'
										})}</button
									>
									{#if usersMonth == month}
										<span
											class="fa-solid fa-check text-logoBackgroundBlue my-auto"
										/>
									{/if}
								</div>
							{/if}
						{/each}
					</fieldset>
				</section>
			</details>
			<span class="text-lg font-medium text-gray-900">/</span>
			<details class="relative" bind:this={yearDetails}>
				<summary
					on:click|preventDefault={toggleYearDetails}
					style="-webkit-details-marker: none; list-style-type: none;"
					class="text-lg font-medium text-gray-900 hover:text-gray-600"
					>{usersYear}</summary
				>

				<section class="absolute z-10 rounded border border-gray-300">
					<fieldset
						class="flex max-h-[25vh] flex-col justify-between space-y-2 overflow-auto rounded bg-white p-2"
					>
						{#each Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + i) as year}
							<div class="flex gap-2">
								<button
									aria-current={usersYear == year}
									on:click={() => {
										usersYear = year;
										toggleYearDetails();
										//Sets focus to the summary element
										yearDetails.querySelector('summary')?.focus();
									}}>{year}</button
								>
								{#if usersYear == year}
									<span
										class="fa-solid fa-check text-logoBackgroundBlue my-auto"
									/>
								{/if}
							</div>
						{/each}
					</fieldset>
				</section>
			</details>
		</div>

		<button
			on:click={() => changeMonth(1)}
			disabled={usersYear == new Date().getFullYear() + 9 && usersMonth == 11}
			aria-label="Next Month"
			class="text-logoBackgroundBlue text-lg font-medium disabled:text-gray-300"
		>
			<span class="fa-solid fa-angle-right"></span>
		</button>
	</div>

	<table class="border-separate border-spacing-4">
		<thead aria-label="Days of the week" class="text-gray-500">
			<tr>
				<th class="font-normal" scope="col">Mo</th>
				<th class="font-normal" scope="col">Tu</th>
				<th class="font-normal" scope="col">We</th>
				<th class="font-normal" scope="col">Th</th>
				<th class="font-normal" scope="col">Fr</th>
				<th class="font-normal" scope="col">Sa</th>
				<th class="font-normal" scope="col">Su</th>
			</tr>
		</thead>
		<tbody
			class="text-center"
			aria-label={'Calendar for ' +
				new Date(0, usersMonth).toLocaleString('default', { month: 'long' }) +
				' ' +
				usersYear}
		>
			{#each monthDays as week}
				<tr>
					{#each week as day}
						{#if day.available}
							{#if chosenDate == usersYear + '-' + (usersMonth + 1) + '-' + day.text}
								<td
									aria-current="date"
									on:click={() => checkDate(usersYear, usersMonth, day.text)}
									class="ring-logoBackgroundBlue rounded-full ring-1 ring-inset"
									>{day.text}</td
								>
							{:else}
								<td
									class="cursor-pointer"
									on:click={() => checkDate(usersYear, usersMonth, day.text)}
									>{day.text}</td
								>
							{/if}
						{:else}
							<td
								aria-disabled="true"
								title={day.tooltip}
								class="cursor-not-allowed text-gray-300">{day.text}</td
							>
						{/if}
					{/each}
				</tr>
			{/each}
		</tbody>
	</table>
</dialog>

<button
	aria-haspopup="dialog"
	class="text-logoBackgroundBlue hover:text-logobackgroundBlueRgba inline-block text-sm underline underline-offset-4 transition"
	aria-label={chosenDate
		? `Change ${$cart.delivery ? 'delivery' : 'collection'} date`
		: `Choose a ${$cart.delivery ? 'delivery' : 'collection'} date`}
	on:click={() => openDialog()}
	>{chosenDate
		? `Change ${$cart.delivery ? 'delivery' : 'collection'} date `
		: `Choose a ${$cart.delivery ? 'delivery' : 'collection'} date `}<span
		class="fa-duotone fa-calendars"
	></span></button
>
